import React, { useState } from 'react';
import { Customer } from '@/hooks/useCustomers';
import { useSourceManager } from '@/hooks/useSourceManager';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface ManualSourceCardProps {
  customer: Customer;
  onSetupMode?: (mode: string) => void;
}

export const ManualSourceCard: React.FC<ManualSourceCardProps> = ({ customer, onSetupMode }) => {
  const { sources, enableSource, disableSource, loading } = useSourceManager(customer.id);
  const [showGuide, setShowGuide] = useState(false);
  
  const manualSource = sources.manual;
  const isActive = manualSource?.status === 'active';
  
  const handleToggle = async () => {
    if (isActive) {
      await disableSource('manual');
    } else {
      await enableSource('manual', { auto_enabled: true });
    }
  };

  const handleStartEntry = () => {
    onSetupMode?.('manual_entry');
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
            <span className="text-xl">✏️</span>
          </div>
          <div>
            <h3 className="font-medium text-foreground">Manual Entry</h3>
            <p className="text-sm text-muted-foreground">Direct data entry for quick bill processing</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={isActive ? "default" : "secondary"}>
            {isActive ? "✅ Active" : "⏸️ Inactive"}
          </Badge>
          <Button
            variant={isActive ? "outline" : "default"}
            size="sm"
            onClick={handleToggle}
            disabled={loading}
          >
            {isActive ? "Disable" : "Enable"}
          </Button>
        </div>
      </div>

      {isActive && (
        <>
          {/* Manual Entry Stats */}
          <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-blue-500/5 rounded">
            <div>
              <div className="text-xs text-blue-600">Entries This Week</div>
              <div className="text-lg font-semibold text-blue-700">12</div>
              <div className="text-xs text-blue-600/80">Manual bills</div>
            </div>
            <div>
              <div className="text-xs text-blue-600">Avg Entry Time</div>
              <div className="text-lg font-semibold text-blue-700">2.5min</div>
              <div className="text-xs text-blue-600/80">Per bill</div>
            </div>
          </div>

          {/* Manual Entry Benefits */}
          <div className="p-3 bg-muted rounded mb-4">
            <div className="text-xs font-medium text-foreground mb-2">✏️ Manual Entry Features</div>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• 🚀 Instant bill creation and processing</li>
              <li>• 📝 Smart form with auto-completion</li>
              <li>• 🔄 Copy from previous bills feature</li>
              <li>• ✅ Real-time validation and error checking</li>
              <li>• 📊 Template-based quick entry</li>
            </ul>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2">
            <Button onClick={handleStartEntry} className="flex-1">
              ✏️ Start Manual Entry
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowGuide(!showGuide)}>
              📖 Guide
            </Button>
          </div>

          {/* Quick Entry Guide */}
          {showGuide && (
            <div className="mt-4 p-3 bg-blue-500/5 rounded border border-blue-500/20">
              <div className="text-xs font-medium text-blue-700 mb-2">📖 Quick Entry Guide</div>
              <div className="text-xs text-blue-600 space-y-1">
                <div>1. Click "Start Manual Entry" to open the entry form</div>
                <div>2. Fill in required fields: Customer, Amount, Bill Date</div>
                <div>3. Use auto-complete for customer and supplier names</div>
                <div>4. Click "Save" to process the bill immediately</div>
                <div>5. Use templates for faster repeated entries</div>
              </div>
            </div>
          )}
        </>
      )}

      {!isActive && (
        <div className="p-3 bg-muted rounded text-center">
          <div className="text-sm text-muted-foreground mb-2">
            Enable manual entry to start creating bills directly
          </div>
          <Button onClick={handleToggle} disabled={loading}>
            {loading ? "Enabling..." : "Enable Manual Entry"}
          </Button>
        </div>
      )}
    </div>
  );
};